import {
  Directive,
  ElementRef,
  forwardRef,
  HostListener,
  Injector,
  OnInit,
} from "@angular/core";
import {
  ControlValueAccessor,
  NG_VALUE_ACCESSOR,
  NgControl,
} from "@angular/forms";
import { markPendingCnpjInvalidCharacter } from "../utils/cnpj-input-state";
import {
  extractValidCnpjChars,
  formatCnpj,
  hasInvalidCnpjCharacters,
} from "../utils/cnpj.utils";

@Directive({
  selector: "input[appCnpjInput]",
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => CnpjInputDirective),
      multi: true,
    },
  ],
})
export class CnpjInputDirective implements ControlValueAccessor, OnInit {
  private onChange: (value: string) => void = () => undefined;
  private onTouched: () => void = () => undefined;
  private ngControl: NgControl | null = null;
  private isApplyingFormattedValue = false;

  constructor(
    private readonly elementRef: ElementRef<HTMLInputElement>,
    private readonly injector: Injector
  ) {}

  ngOnInit(): void {
    this.ngControl = this.injector.get(NgControl, null);
  }

  writeValue(value: string | null): void {
    this.setInputValue(formatCnpj(value));
  }

  registerOnChange(fn: (value: string) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.elementRef.nativeElement.disabled = isDisabled;
  }

  @HostListener("input", ["$event"])
  onInput(event: Event): void {
    this.applyValueFromRaw((event.target as HTMLInputElement).value);
  }

  @HostListener("paste", ["$event"])
  onPaste(event: ClipboardEvent): void {
    event.preventDefault();

    const pastedText = event.clipboardData?.getData("text") ?? "";
    const input = this.elementRef.nativeElement;
    const selectionStart = input.selectionStart ?? 0;
    const selectionEnd = input.selectionEnd ?? 0;
    const mergedValue =
      input.value.slice(0, selectionStart) +
      pastedText +
      input.value.slice(selectionEnd);

    this.applyValueFromRaw(mergedValue);
  }

  @HostListener("blur")
  onBlur(): void {
    this.onTouched();
    this.ngControl?.control?.updateValueAndValidity();
  }

  private applyValueFromRaw(rawValue: string): void {
    if (this.isApplyingFormattedValue) {
      return;
    }

    if (hasInvalidCnpjCharacters(rawValue)) {
      this.markInvalidCharacterAttempt();
    }

    const formatted = formatCnpj(extractValidCnpjChars(rawValue));

    this.setInputValue(formatted);
    this.onChange(formatted);
  }

  private setInputValue(value: string): void {
    this.isApplyingFormattedValue = true;
    this.elementRef.nativeElement.value = value;
    this.isApplyingFormattedValue = false;
  }

  private markInvalidCharacterAttempt(): void {
    const control = this.ngControl?.control;
    if (control) {
      markPendingCnpjInvalidCharacter(control);
    }
  }
}
